

import { useState } from 'react';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyType: '',
    location: '',
    photoPackage: '',
    urgency: 'standard',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const packagePricing = {
    '1-5': { price: 250, label: '1-5 Photos - $250' },
    '6-10': { price: 300, label: '6-10 Photos - $300' },
    '11-15': { price: 350, label: '11-15 Photos - $350' },
    '16-20': { price: 400, label: '16-20 Photos - $400' },
    'custom': { price: 0, label: '20+ Photos - Custom Quote' }
  };

  const rushFees = {
    'standard': { fee: 0, label: 'Standard (3-5 days)' },
    '24-hour': { fee: 50, label: '24-Hour Rush (+$50)' },
    '12-hour': { fee: 100, label: '12-Hour Rush (+$100)' }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateTotal = () => {
    if (!formData.photoPackage || formData.photoPackage === 'custom') return 0;
    const basePrice = packagePricing[formData.photoPackage as keyof typeof packagePricing]?.price || 0;
    const rushFee = rushFees[formData.urgency as keyof typeof rushFees]?.fee || 0;
    return basePrice + rushFee;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      if (formData.photoPackage === 'custom') {
        // Submit custom quote request
        const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/submit-quote-request`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            customerName: formData.name,
            customerEmail: formData.email,
            customerPhone: formData.phone,
            propertyType: formData.propertyType,
            location: formData.location,
            photoCount: '20+',
            urgency: formData.urgency,
            message: formData.message
          })
        });

        const result = await response.json();

        if (response.ok) {
          setSubmitMessage('Quote request submitted! We\'ll contact you within 24 hours with a custom quote and upload instructions.');
          setFormData({
            name: '',
            email: '',
            phone: '',
            propertyType: '',
            location: '',
            photoPackage: '',
            urgency: 'standard',
            message: ''
          });
        } else {
          throw new Error(result.error || 'Failed to submit quote request');
        }
      } else {
        // Create payment session for standard package
        const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-payment-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            customerName: formData.name,
            customerEmail: formData.email,
            customerPhone: formData.phone,
            propertyType: formData.propertyType,
            location: formData.location,
            photoPackage: formData.photoPackage,
            urgency: formData.urgency,
            message: formData.message
          })
        });

        const result = await response.json();

        if (response.ok && result.url) {
          // Redirect to Stripe checkout
          window.location.href = result.url;
        } else {
          throw new Error(result.error || 'Failed to create payment session');
        }
      }
    } catch (error) {
      setSubmitMessage(`Error: ${error instanceof Error ? error.message : 'Something went wrong'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.name && formData.email && formData.propertyType &&
    formData.location && formData.photoPackage;

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Get Professional Photo Editing</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Upload your real estate photos and get professionally edited images that sell properties faster
          </p>
        </div>

        {/* Pricing Display Section */}
        <div className="max-w-6xl mx-auto mb-16">
          <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">Photo Editing Packages & Pricing</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {Object.entries(packagePricing).filter(([key]) => key !== 'custom').map(([key, pkg]) => (
              <div key={key} className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100 hover:shadow-lg transition-shadow">
                <div className="text-center">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">{key.replace('-', '-')} Photos</h4>
                  <div className="text-3xl font-bold text-blue-600 mb-3">${pkg.price}</div>
                  <p className="text-sm text-gray-600">Professional editing for {key === '1-5' ? 'small listings' : key === '6-10' ? 'apartments & condos' : key === '11-15' ? 'standard homes' : 'large properties'}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="bg-amber-50 rounded-xl p-6 border border-amber-200 max-w-md mx-auto">
            <div className="text-center">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">20+ Photos</h4>
              <div className="text-2xl font-bold text-amber-600 mb-3">Custom Quote</div>
              <p className="text-sm text-gray-600">For luxury estates and commercial properties</p>
            </div>
          </div>

          <div className="mt-8 bg-gray-50 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">Turnaround Time Options</h4>
            <div className="grid md:grid-cols-3 gap-4">
              {Object.entries(rushFees).map(([key, rush]) => (
                <div key={key} className="text-center">
                  <div className="font-medium text-gray-900">{rush.label.split('(')[0].trim()}</div>
                  <div className="text-sm text-gray-600">{rush.fee > 0 ? `+$${rush.fee}` : 'Included'}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Process Overview Section */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 mb-8 border border-blue-100">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">How It Works</h3>
              <p className="text-gray-600">Simple 3-step process to get professionally edited real estate photos</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-xl">1</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Pay &amp; Upload</h4>
                <p className="text-sm text-gray-600">Choose your package, complete secure payment, then upload your raw photos using the secure link we'll email you</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-xl">2</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">AI Enhancement</h4>
                <p className="text-sm text-gray-600">Our professional editors enhance your photos with HDR processing, color correction, and virtual staging</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-xl">3</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Download Ready</h4>
                <p className="text-sm text-gray-600">Access your professionally edited photos through our secure client portal link sent to your email</p>
              </div>
            </div>
            
            <div className="mt-8 bg-white rounded-lg p-6 border border-blue-200">
              <div className="flex items-start">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-1">
                  <i className="ri-upload-cloud-line text-green-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Ready to Upload Your Photos?</h4>
                  <p className="text-gray-600">
                    <strong>After payment, you'll receive a secure upload link via email.</strong> Simply drag and drop your raw real estate photos, 
                    and we'll transform them into stunning, professional images that help sell properties faster.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="bg-gray-50 rounded-2xl p-8 shadow-lg">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">Start Your Photo Editing Order</h3>
              <p className="text-gray-600">Fill out the form below to get professional photo editing</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="propertyType" className="block text-sm font-medium text-gray-700 mb-2">
                  Property Type *
                </label>
                <select
                  id="propertyType"
                  name="propertyType"
                  value={formData.propertyType}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                >
                  <option value="">Select property type</option>
                  <option value="residential">Residential Home</option>
                  <option value="condo">Condo/Apartment</option>
                  <option value="commercial">Commercial Property</option>
                  <option value="luxury">Luxury Estate</option>
                  <option value="land">Land/Lot</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                  Property Location *
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="City, State or full address"
                />
              </div>

              <div>
                <label htmlFor="photoPackage" className="block text-sm font-medium text-gray-700 mb-2">
                  Photo Editing Package *
                </label>
                <select
                  id="photoPackage"
                  name="photoPackage"
                  value={formData.photoPackage}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                >
                  <option value="">Select package</option>
                  {Object.entries(packagePricing).map(([key, pkg]) => (
                    <option key={key} value={key}>{pkg.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="urgency" className="block text-sm font-medium text-gray-700 mb-2">
                  Turnaround Time
                </label>
                <select
                  id="urgency"
                  name="urgency"
                  value={formData.urgency}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                >
                  {Object.entries(rushFees).map(([key, rush]) => (
                    <option key={key} value={key}>{rush.label}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Special Instructions
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Any specific editing requests, style preferences, or special instructions..."
                  maxLength={500}
                />
                <p className="text-sm text-gray-500 mt-1">{formData.message.length}/500 characters</p>
              </div>
            </div>

            {/* Pricing Summary */}
            {formData.photoPackage && formData.photoPackage !== 'custom' && (
              <div className="bg-blue-50 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Pricing Summary</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Photo Editing ({formData.photoPackage} photos)</span>
                    <span>${packagePricing[formData.photoPackage as keyof typeof packagePricing]?.price}</span>
                  </div>
                  {formData.urgency !== 'standard' && (
                    <div className="flex justify-between">
                      <span>Rush Fee ({rushFees[formData.urgency as keyof typeof rushFees]?.label})</span>
                      <span>+${rushFees[formData.urgency as keyof typeof rushFees]?.fee}</span>
                    </div>
                  )}
                  <div className="border-t pt-2 flex justify-between font-bold text-lg">
                    <span>Total</span>
                    <span>${calculateTotal()}</span>
                  </div>
                </div>
              </div>
            )}

            {formData.photoPackage === 'custom' && (
              <div className="bg-amber-50 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Custom Quote Request</h3>
                <p className="text-gray-700">
                  For projects requiring 20+ photos, we'll provide a custom quote within 24 hours. 
                  Our team will contact you to discuss your specific editing needs and provide competitive pricing.
                </p>
              </div>
            )}

            {submitMessage && (
              <div className={`p-4 rounded-lg mb-6 ${
                submitMessage.includes('Error')
                  ? 'bg-red-50 text-red-700 border border-red-200'
                  : 'bg-green-50 text-green-700 border border-green-200'
              }`}>
                {submitMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              className="w-full bg-blue-600 text-white py-4 px-8 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Processing...
                </span>
              ) : formData.photoPackage === 'custom' ? (
                'Request Custom Quote'
              ) : formData.photoPackage ? (
                `Pay $${calculateTotal()} & Get Upload Link`
              ) : (
                'Select Package to Continue'
              )}
            </button>

            <div className="mt-6 bg-blue-50 rounded-lg p-4">
              <div className="flex items-center justify-center text-sm text-gray-600">
                <i className="ri-shield-check-line text-blue-600 mr-2"></i>
                <span>
                  {formData.photoPackage === 'custom'
                    ? 'We\'ll contact you within 24 hours with a quote and upload instructions'
                    : 'After payment, you\'ll receive a secure upload link via email to submit your photos'}
                </span>
              </div>
            </div>

            <p className="text-sm text-gray-500 text-center mt-4">
              {formData.photoPackage === 'custom'
                ? 'Questions? Call us at (510) 641-8648 or email info@vistaboostimaging.com'
                : 'Secure payment processing powered by Stripe • Questions? Call (510) 641-8648'}
            </p>
          </form>
        </div>

      </div>
    </section>
  );
};

export default ContactSection;
export { ContactSection };

